import React from "react";
import {Box, ThemeContext} from "grommet";
import {normalizeColor} from "grommet/utils";
import {
    ICON_WIDTH_DEFAULT,
    ICON_WIDTH_LARGE,
    ICON_WIDTH_MEDIUM,
    ICON_WIDTH_SMALL,
    ICON_WIDTH_XLARGE,
    ICON_WIDTH_XSMALL,
    ICON_WIDTH_XXLARGE,
    ICON_WIDTH_XXSMALL,
    ICON_WIDTH_XXXSMALL
} from "../constants/Dimensions";
import { IconArrowMerge, IconArrowUpRight, IconBuildingStore, IconLink } from '@tabler/icons-react';

/*
    Renders a Tabler icon component (e.g. IconTrash from @tabler/icons-react)
    at the app's named sizes and theme colours. Was FontAwesomeIcon until the
    move to Tabler; the props are unchanged.

    Stroke: 1.5 by default (a light line), 2 with `bold`, or whatever
    `stroke` says (overrides both).
*/

const ICON_SIZES = {
    xxxsmall: ICON_WIDTH_XXXSMALL,
    xxsmall: ICON_WIDTH_XXSMALL,
    xsmall: ICON_WIDTH_XSMALL,
    small: ICON_WIDTH_SMALL,
    medium: ICON_WIDTH_MEDIUM,
    large: ICON_WIDTH_LARGE,
    xlarge: ICON_WIDTH_XLARGE,
    xxlarge: ICON_WIDTH_XXLARGE,
};

export const STROKE_DEFAULT = 1.5;
export const STROKE_BOLD = 2;

// Tabler draws on a 24px grid with ~2px of padding, so its glyphs fill ~81% of
// the box where FontAwesome's filled ~100%. Cropping the padding scales each
// glyph up 1.2x without changing the box (so layouts don't move). Strokes are
// in viewBox units, so divide by the same factor to keep the on-screen weight.
const GLYPH_CROP = { x: 2, y: 2, size: 20 };

// A few glyphs sit off-centre next to text. The external-link arrow spans only
// 7-17 of the 24 grid, which left a visible gap between it and the link text,
// so it gets a tighter crop shifted left: a touch larger and flush to the text.
const GLYPH_CROP_OVERRIDES = new Map([
    [IconArrowUpRight, { x: 6, y: 4, size: 16 }],
    // These replaced wide, short FA glyphs (link-simple, merge, shop) and came
    // out 24-29% taller. A looser crop brings them back to the FA height.
    [IconLink, { x: -1, y: -1, size: 26 }],
    [IconArrowMerge, { x: -1, y: -1, size: 26 }],
    [IconBuildingStore, { x: -0.5, y: -0.5, size: 25 }],
]);

// Highlight circle: a touch larger than the icon box (28px for a 24px icon) so
// the icon can stay near its FontAwesome size (19px) without its corners
// poking past the circle. The icon keeps its own colour (text by default) on the
// tinted circle.
const HIGHLIGHT_CIRCLE_SCALE = 7 / 6;
const HIGHLIGHT_ICON_SCALE = 19 / 24;

// rotation / flip as the individual CSS rotate and scale properties rather than
// transform, so they combine with the spin animation (which animates transform)
// instead of being replaced by it while an icon spins.
const FLIP_SCALE = { horizontal: '-1 1', vertical: '1 -1', both: '-1 -1' };

const Icon = ({
    SelectedIcon,
    size = 'medium',
    customSize,
    color="text",
    bold=false,
    stroke,
    highlight,
    highlightProps={},
    styleProps,
    iconContainerProps,
    additionalClass='',
    className,
    rotation,
    flip,
    spin=false,
    ...props
}) => {
    if (!SelectedIcon) {
        // Say so rather than draw an empty box: a caller passing the icon under another
        // prop name (FontAwesome's `icon=`) otherwise renders nothing, silently.
        if (process.env.NODE_ENV !== 'production') {
            console.error('Icon: SelectedIcon is missing', props);
        }
        return null;
    }

    const size_px = customSize || ICON_SIZES[size] || ICON_WIDTH_DEFAULT;
    const strokeWidth = stroke ?? (bold ? STROKE_BOLD : STROKE_DEFAULT);
    const orientation = {
        ...(rotation ? { rotate: `${rotation}deg` } : {}),
        ...(flip ? { scale: FLIP_SCALE[flip === true ? 'horizontal' : flip] } : {}),
    };
    const crop = GLYPH_CROP_OVERRIDES.get(SelectedIcon) || GLYPH_CROP;
    const classes = [spin ? 'icon-spin' : null, className].filter(Boolean).join(' ') || undefined;

    return (
        <ThemeContext.Consumer>
            { (theme) => {
                const iconSize = highlight ? Math.round(size_px * HIGHLIGHT_ICON_SCALE) : size_px;
                const circleSize = Math.round(size_px * HIGHLIGHT_CIRCLE_SCALE);
                const svg = (
                    <SelectedIcon
                        size={iconSize}
                        color={normalizeColor(theme.global.colors[color], theme)}
                        stroke={strokeWidth * crop.size / 24}
                        viewBox={`${crop.x} ${crop.y} ${crop.size} ${crop.size}`}
                        className={classes}
                        style={{
                            flexShrink: 0,
                            // Strokes that touch the cropped edge stay visible
                            overflow: 'visible',
                            transition: '.15s',
                            ...orientation,
                            ...styleProps,
                        }}
                        {...props}
                    />
                );

                // flex={false}: Grommet gives every Box min-width 0, so beside text long
                // enough to wrap, the container squeezed below the svg and the glyph
                // overflowed onto the text.
                return (
                    <Box flex={false} {...iconContainerProps}>
                        {highlight ? (
                            <Box
                                margin={{vertical: 'xsmall'}}
                                width={`${circleSize}px`}
                                height={`${circleSize}px`}
                                flex={false}
                                round="full"
                                align="center"
                                justify="center"
                                background={normalizeColor(theme.global.colors[highlight], theme)}
                                className={additionalClass || undefined}
                                {...highlightProps}
                            >
                                {svg}
                            </Box>
                        ) : svg}
                    </Box>
                );
            }}
        </ThemeContext.Consumer>
    );
};

export default Icon;
